import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  Divider,
  Group,
  Flex,
  Modal,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { IconCheck, IconX, IconBell } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import entityColor from '@util/entityColor';

export default function EffortStatus({
  effortId = null,
  fetchData,
  statusOnly,
  isRideChallenge,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const { data } = useQuery({
    initialData: null,
    queryKey: [`efforts${effortId}Checkup`],
    queryFn: async () =>
      axios
        .post(`/efforts/${effortId}/data-grabber/`, {
          context: 'effort_checkup',
        })
        .then(({ data }) => {
          return data.response[0];
        })
        .catch((err) => {
          throw err;
        }),
  });

  function onStatusClick(newStatus) {
    const req = {
      effort_id: effortId,
      status: newStatus,
    };

    setLoading(true);

    axios
      .put(`/efforts/${effortId}/set-status/`, req)
      .then(({ data }) => {
        if (data?.response?.[0]?.redirect) {
          window.location = `/efforts`;
          toast.success(t('common:saved') + '!');
        } else {
          setLoading(false);
          toast.success(t('common:saved') + '!');
          fetchData();
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  if (!data) return null;

  const publishedAppConfigEmpty = !data.live_status.published_app_configuration
    ? true
    : false;

  const items = [
    { text: t('common:active'), value: data.live_status.active },
    { text: t('common:live'), value: data.live_status.is_live },
    { text: t('common:turned_on'), value: data.live_status.turned_on },
    {
      text: t('mixer:effort:published_configuration'),
      value: data.live_status.published_configuration,
    },
    {
      text: t('mixer:effort:published_app_configuration'),
      value: data.live_status.published_app_configuration,
      visible: publishedAppConfigEmpty ? false : true,
    },
    {
      text: t('mixer:effort:campaign_live'),
      value: data.live_status.campaign_is_live,
      visible: data.campaign_id ? true : false,
    },
  ].filter((f) => f.visible !== false);

  const statusBtns = [
    // { text: "created", value: 1, color: "yellow" },
    { text: t('common:on'), value: 2, color: 'green' },
    { text: t('common:off'), value: 3, color: 'red' },
    { text: t('common:deleted'), value: 4, color: 'yellow' },
  ].map((m) => ({
    ...m,
    key: m.value,
  }));

  if (statusOnly) {
    return (
      <Card>
        <SectionDivider text={t('common:status')} />
        <Group spacing="xs">
          {statusBtns.map((b) => (
            <Button
              key={b.key}
              onClick={() => onStatusClick(b.value)}
              color={b.color}
              size="xs"
              variant={data.status === b.value ? 'filled' : 'light'}
            >
              {b.text}
            </Button>
          ))}
        </Group>
      </Card>
    );
  }

  return (
    <Card>
      <SectionDivider
        text={
          isRideChallenge
            ? t('mixer:effort:ride_challenge_checkup')
            : t('mixer:effort:effort_checkup')
        }
      />
      <div>
        {items.map((item, i) => (
          <Flex key={i} align="center" gap="xs" mb="xs">
            <ThemeIcon
              variant="filled"
              radius="xl"
              size="sm"
              color={item.value ? 'green' : 'red'}
            >
              {item.value ? <IconCheck /> : <IconX />}{' '}
            </ThemeIcon>
            <Text>{item.text}</Text>
          </Flex>
        ))}
      </div>
      {/* {data.campaign_id && (
        <React.Fragment>
          <SectionDivider text="App Config" />
          <AppConfigButtons
            effortId={effortId}
            onSuccess={fetchData}
            usingEffortConfig={data.using_effort_app_config}
            showAppConfigWarning={data.show_app_config_warning}
          />
        </React.Fragment>
      )} */}
      <SectionDivider text="Status" />
      <Group spacing="xs">
        {statusBtns.map((b) => (
          <Button
            key={b.key}
            onClick={() => onStatusClick(b.value)}
            color={b.color}
            size="xs"
            variant={data.status === b.value ? 'filled' : 'light'}
          >
            {b.text}
          </Button>
        ))}
      </Group>
      <SectionDivider text={t('common:config')} />
      <PublishButton effortId={effortId} onSuccess={fetchData} />
      {data.has_config_to_publish && (
        <Flex mt="sm" gap="xs" align="center">
          <ThemeIcon
            // variant="filled"
            variant="light"
            radius="xl"
            mr="xs"
            size="sm"
            color="pink.4"
          >
            <IconBell />
          </ThemeIcon>
          <Text size="xs">{t('mixer:effort:unpublished_config_changes')}</Text>
        </Flex>
      )}
      <SectionDivider text={`${t('common:config')} App`} />
      <PublishAppConfigButton effortId={effortId} onSuccess={fetchData} />
      {data.has_app_config_to_publish && (
        <Flex mt="sm" gap="xs" align="center">
          <ThemeIcon
            // variant="filled"
            variant="light"
            radius="xl"
            mr="xs"
            size="sm"
            color="pink.4"
          >
            <IconBell />
          </ThemeIcon>
          <Text style={{ display: 'flex', alignItems: 'center' }} size="xs">
            {t('mixer:effort:unpublished_app_config_changes')}
          </Text>
        </Flex>
      )}
    </Card>
  );
}

export const PublishButton = ({
  effortId,
  onSuccess,
  buttonProps = {
    size: 'xs',
    color: 'green',
  },
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  function onClick() {
    setLoading(true);
    axios
      .put(`/efforts/${effortId}/publish-config/`, {})
      .then(() => {
        setLoading(false);
        toast.success(t('common:published') + '!');
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  return (
    <Button {...buttonProps} loading={loading} onClick={onClick}>
      {t('mixer:effort:publish_config')}
    </Button>
  );
};

const PublishAppConfigButton = ({ effortId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  function onClick() {
    setLoading(true);
    axios
      .put(`/efforts/${effortId}/publish-app-config/`, {})
      .then(() => {
        setLoading(false);
        toast.success(t('common:published') + '!');
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  return (
    <Button loading={loading} onClick={onClick} size="xs" color="green">
      {t('mixer:effort:publish_app_config')}
    </Button>
  );
};

const SectionDivider = ({ text }) => <Divider my="sm" label={text} />;

const AppConfigButtons = ({
  usingEffortConfig,
  effortId,
  onSuccess,
  showAppConfigWarning,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  function onModalClose() {
    setShowModal(false);
  }

  function toggleConfig(confirmed = false) {
    if (showAppConfigWarning && !confirmed) return setShowModal(true);

    setLoading(true);

    axios
      .post(`/efforts/${effortId}/toggle-app-config/`, {})
      .then(() => {
        setLoading(false);
        setShowModal(false);
        toast.success(t('common:saved') + '!');
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }
  return (
    <>
      <Modal opened={showModal} onClose={onModalClose}>
        <Text align="center">
          This action will <b>turn this effort off</b>.
        </Text>
        <Text mt="sm" align="center">
          Are you sure this is what you want?
        </Text>
        <Divider mt="lg" mb="lg" />
        <Button color="red" fullWidth mb="sm" onClick={onModalClose}>
          Nope! Get me out of here!
        </Button>
        <Group justify="center">
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            onClick={() => toggleConfig(true)}
            loading={loading}
          >
            I'm sure
          </Button>
        </Group>
      </Modal>
      <Group>
        <Button
          size="xs"
          onClick={() => toggleConfig()}
          variant={usingEffortConfig ? 'filled' : 'subtle'}
          color={usingEffortConfig ? entityColor.effort : 'gray'}
        >
          Effort
        </Button>
        <Button
          size="xs"
          onClick={() => toggleConfig()}
          variant={!usingEffortConfig ? 'filled' : 'subtle'}
          color={!usingEffortConfig ? entityColor.campaign : 'gray'}
        >
          Campaign
        </Button>
      </Group>
    </>
  );
};
